import type { ConcertOutcomeTone } from "../concert/concert-application-copy"

/**
 * Copy for the Sungage hosted application every Proposal Kind shows: the
 * status drawer's labels and the one reading of a Sungage credit status,
 * review flag and project progress. A kind names its program in it.
 */

export const SUNGAGE_MAX_PAYMENT_CONDITION = `if the deferred portion is not paid`
export const SUNGAGE_ASSUMED_PAYOFF_NOTE = `Assumed payoff, not an assured tax credit`
export const SUNGAGE_ACH_ASSUMED_LABEL = `ACH assumed`
export const SUNGAGE_NEXT_STEP_LABEL = `Next step`
export const SUNGAGE_RESUME_LABEL = `Continue application`
export const SUNGAGE_RESUME_BUSY_LABEL = `Opening application`
export const SUNGAGE_HOSTED_APPLICATION_TITLE = `Sungage application`
export const SUNGAGE_HOSTED_URL_UNAVAILABLE = `Sungage did not return a usable borrower application URL.`
export const SUNGAGE_UNCERTAIN_PROMPT = `Sungage project identity is not confirmed. Contact support before retrying.`
export const SUNGAGE_TERMS_CHANGED_LABEL = `Approved terms differ from the original submitted terms.`
export const SUNGAGE_FINANCE_PLAN_MISMATCH_LABEL = `The selected Finance Partner no longer matches this Sungage application.`
export const SUNGAGE_REVIEW_NEEDED_LABEL = `Review needed`
export const SUNGAGE_CREDIT_HEADING = `Credit`
export const SUNGAGE_SUBMITTED_TERMS_HEADING = `Submitted terms`
export const SUNGAGE_APPROVED_TERMS_HEADING = `Approved terms`
export const SUNGAGE_CHECK_UNAVAILABLE = `unavailable`
export const SUNGAGE_REFRESH_FAILED_PROMPT = `Sungage could not be reached. The last saved status and terms are still shown.`
export const SUNGAGE_ESTIMATE_LABEL = `Current estimate`
export const SUNGAGE_VERIFIED_APPROVED_LABEL = `Verified approved terms`
export const SUNGAGE_PROVIDER = `sungage` as const

export type SungageOutcomeTone = ConcertOutcomeTone

export type SungageOutcome = {
  badge: string
  tone: SungageOutcomeTone
  title: string
  detail: string
  /** Support is the only next step; nothing on the proposal fixes it. */
  supportNeeded: boolean
  /** The homeowner still has the hosted application to finish. */
  continueApplication: boolean
}

/** What the outcome reads from any kind's application. */
export type SungageOutcomeInput = {
  creditStatus: string
  projectProgress: { dateFunded: string | null; webhookEventName: string | null }
  reviewNeeded: boolean
  reviewReasons: string[]
}

/**
 * One reading of the saved Sungage credit status, review flag, and project
 * progress. Funding and signing outrank the credit status they follow.
 */
export const sungageApplicationOutcome = (
  application: SungageOutcomeInput,
  /** The program the loan is, in the funded / approved / declined copy. */
  programLabel: string,
): SungageOutcome => {
  const { creditStatus, reviewNeeded, reviewReasons, projectProgress } =
    application

  if (creditStatus === `verified-withdrawal`) {
    return {
      badge: `Withdrawn`,
      tone: `muted`,
      title: `Application withdrawn`,
      detail: `Sungage confirmed this application was withdrawn.`,
      supportNeeded: false,
      continueApplication: false,
    }
  }

  if (reviewNeeded || creditStatus === `review-needed`) {
    return {
      badge: SUNGAGE_REVIEW_NEEDED_LABEL,
      tone: `gold`,
      title: `Sungage status needs review`,
      detail:
        reviewReasons.length > 0
          ? reviewReasons.join(`. `)
          : SUNGAGE_UNCERTAIN_PROMPT,
      supportNeeded: true,
      continueApplication: false,
    }
  }

  if (projectProgress.dateFunded != null) {
    return {
      badge: `Funded`,
      tone: `success`,
      title: `Loan funded`,
      detail: `Sungage funded this ${programLabel} loan.`,
      supportNeeded: false,
      continueApplication: false,
    }
  }

  if (projectProgress.webhookEventName === `Loan document signed`) {
    return {
      badge: `Documents signed`,
      tone: `success`,
      title: `Loan documents signed`,
      detail: `The homeowner signed the Sungage loan documents. Sungage funds the loan after installation milestones.`,
      supportNeeded: false,
      continueApplication: false,
    }
  }

  switch (creditStatus) {
    case `Not yet submitted`:
      return {
        badge: `Application started`,
        tone: `gold`,
        title: `Application started`,
        detail: `The homeowner has not submitted the Sungage application yet. Continue the application to finish it.`,
        supportNeeded: false,
        continueApplication: true,
      }
    case `Under review`:
      return {
        badge: `Under review`,
        tone: `muted`,
        title: `Sungage is reviewing the application`,
        detail: `Sungage has the submitted application. This status refreshes while the drawer is open.`,
        supportNeeded: false,
        continueApplication: false,
      }
    case `Approved (stips pending)`:
      return {
        badge: `Approved with stipulations`,
        tone: `gold`,
        title: `Approved, stipulations pending`,
        detail: `Sungage approved the application. The stipulations listed here must clear before the loan moves forward.`,
        supportNeeded: false,
        continueApplication: false,
      }
    case `Approved`:
      return {
        badge: `Approved`,
        tone: `success`,
        title: `Application approved`,
        detail: `Sungage approved this ${programLabel} application.`,
        supportNeeded: false,
        continueApplication: false,
      }
    case `Ineligible for financing`:
      return {
        badge: `Declined`,
        tone: `danger`,
        title: `Not eligible for financing`,
        detail: `Sungage found this application ineligible for ${programLabel} financing.`,
        supportNeeded: false,
        continueApplication: false,
      }
    default:
      return {
        badge: SUNGAGE_REVIEW_NEEDED_LABEL,
        tone: `gold`,
        title: `Sungage status needs review`,
        detail: `Sungage returned a credit status Vision does not recognize: ${creditStatus}.`,
        supportNeeded: true,
        continueApplication: false,
      }
  }
}
